import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { MEASURE_DEFINITION_BY_TITLE } from '../analyticsDefinitions'
import { EXPLORER_TYPE, TREND_TYPE } from '../renderTypes'
import { OrderValue } from '~/types/cube'
import { useCustomizationStore } from '~/stores/customization'
import { CustomizationField } from '~/types/customization'
import {
  DashboardModuleType,
  type DashboardConfig,
  type ModuleConfig
} from '~/types/dashboards'

export const createDeiDashboardConfig = (): DashboardConfig => {
  const customizationStore = useCustomizationStore()
  const employeeGroupDimension = customizationStore.getCustomizationByField(
    CustomizationField.EMPLOYEE_GROUP
  )

  const isManagerDataAvailable = customizationStore.isManagerDataAvailable

  return [
    {
      titleKey: 'dashboard.keyFigures',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.dei.ratioGenderEmployee.title',
              trendType: TREND_TYPE.RATIO,
              iconKeys: [
                'dashboard.dei.icons.female',
                'dashboard.dei.icons.male'
              ],
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END,
                dimension: employeeGroupDimension,
                group: DIMENSION_TITLES.EMPLOYEE_GENDER,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_COUNT_END]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_RATIO_GENDER_F,
                MEASURE_TITLES.EMPLOYEE_RATIO_GENDER_M
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.dei.avgAge.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_AGE,
                type: EXPLORER_TYPE.LIST
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_AGE
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_AGE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.PYRAMID,
          options: {
            config: {
              titleKey: 'dashboard.dei.agePyramid.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END,
                type: EXPLORER_TYPE.BAR,
                dimension: DIMENSION_TITLES.EMPLOYEE_AGE_RANGE,
                group: DIMENSION_TITLES.EMPLOYEE_GENDER,
                sort: true
              }
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_COUNT_END_F,
                MEASURE_TITLES.EMPLOYEE_COUNT_END_M
              ],
              dimensions: [DIMENSION_TITLES.EMPLOYEE_AGE_RANGE],
              order: [[DIMENSION_TITLES.EMPLOYEE_AGE_RANGE, OrderValue.DESC]]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.dei.ratioGenderArrival.title',
              trendType: TREND_TYPE.RATIO,
              iconKeys: [
                'dashboard.dei.icons.female',
                'dashboard.dei.icons.male'
              ],
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL,
                dimension: employeeGroupDimension,
                group: DIMENSION_TITLES.EMPLOYEE_GENDER,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL
                ]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_RATIO_ARRIVAL_F,
                MEASURE_TITLES.EMPLOYEE_RATIO_ARRIVAL_M
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.dei.nationalities.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END,
                dimension: DIMENSION_TITLES.EMPLOYEE_NATIONALITY,
                type: EXPLORER_TYPE.KPI,
                sort: true
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END_NATIONALITY]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.moreData',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.dei.averageSalary.title',
              iconKeys: [
                'dashboard.dei.icons.female',
                'dashboard.dei.icons.male'
              ],
              exploreLink: {
                measure:
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_TOTAL_CONVERTIBLE,
                dimension: DIMENSION_TITLES.EMPLOYEE_GENDER,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_TOTAL_CONVERTIBLE
                ]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_F_CONVERTIBLE,
                MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_M_CONVERTIBLE
              ]
            }
          }
        },
        ...(isManagerDataAvailable
          ? [
              {
                moduleType: DashboardModuleType.DOUBLE,
                options: {
                  config: {
                    titleKey: 'dashboard.dei.managers.title',
                    firstSubtitleKey:
                      'dashboard.dei.managers.ratioGenderManager.title',
                    firstTrendType: TREND_TYPE.RATIO,
                    secondSubtitleKey:
                      'dashboard.dei.managers.ratioManagerGender.title',
                    secondTrendType: TREND_TYPE.RATIO,
                    iconKeys: [
                      'dashboard.dei.icons.female',
                      'dashboard.dei.icons.male'
                    ],
                    exploreLink: {
                      measure: MEASURE_TITLES.EMPLOYEE_COUNT_END_MANAGER,
                      dimension: DIMENSION_TITLES.EMPLOYEE_GENDER,
                      type: EXPLORER_TYPE.BAR
                    },
                    infoContent:
                      MEASURE_DEFINITION_BY_TITLE[
                        MEASURE_TITLES.EMPLOYEE_COUNT_END_MANAGER
                      ]
                  },
                  firstQuery: {
                    measures: [
                      MEASURE_TITLES.EMPLOYEE_RATIO_GENDER_MANAGER_F,
                      MEASURE_TITLES.EMPLOYEE_RATIO_GENDER_MANAGER_M
                    ]
                  },
                  secondQuery: {
                    measures: [
                      MEASURE_TITLES.EMPLOYEE_RATIO_MANAGER_F,
                      MEASURE_TITLES.EMPLOYEE_RATIO_MANAGER_M
                    ]
                  }
                }
              } satisfies ModuleConfig
            ]
          : []),
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.dei.attrition.title',
              iconKeys: [
                'dashboard.dei.icons.female',
                'dashboard.dei.icons.male'
              ],
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_ATTRITION,
                dimension: DIMENSION_TITLES.EMPLOYEE_GENDER,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_ATTRITION]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_ATTRITION_F,
                MEASURE_TITLES.EMPLOYEE_ATTRITION_M
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.dei.averageSalaryRaise.title',
              trendType: TREND_TYPE.RATIO,
              iconKeys: [
                'dashboard.dei.icons.female',
                'dashboard.dei.icons.male'
              ],
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_RAISE_RATE,
                dimension: DIMENSION_TITLES.EMPLOYEE_GENDER,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_RAISE_RATE
                ]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_RAISE_RATE_F,
                MEASURE_TITLES.EMPLOYEE_AVERAGE_SALARY_BASE_RAISE_RATE_M
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.dei.turnover.title',
              iconKeys: [
                'dashboard.dei.icons.female',
                'dashboard.dei.icons.male'
              ],
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_TURNOVER,
                dimension: DIMENSION_TITLES.EMPLOYEE_GENDER,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_TURNOVER]
            },
            query: {
              measures: [
                MEASURE_TITLES.EMPLOYEE_TURNOVER_F,
                MEASURE_TITLES.EMPLOYEE_TURNOVER_M
              ]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.dei.genderBaseSalaryGapRate.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_GENDER_BASE_SALARY_GAP_RATE,
                dimension: employeeGroupDimension,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_GENDER_BASE_SALARY_GAP_RATE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_GENDER_BASE_SALARY_GAP_RATE]
            }
          }
        }
      ]
    }
  ]
}
